<template>
  <div
    ref="rootRef"
    class="wa-quick-messages-popover"
    role="dialog"
    :aria-label="t('kanban.leadDetail.whatsapp.quickMessages.title')"
    @click.stop
  >
    <div class="wa-quick-messages-popover__header">
      <div class="wa-quick-messages-popover__heading">
        <span class="wa-quick-messages-popover__icon" aria-hidden="true">
          <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z"/>
          </svg>
        </span>
        <div class="wa-quick-messages-popover__heading-text">
          <span class="wa-quick-messages-popover__title">{{ t('kanban.leadDetail.whatsapp.quickMessages.title') }}</span>
          <span v-if="!loading && messages.length" class="wa-quick-messages-popover__count">
            {{ t('kanban.leadDetail.whatsapp.quickMessages.count', { count: messages.length }) }}
          </span>
          <span v-else-if="!loading" class="wa-quick-messages-popover__subtitle">
            {{ t('kanban.leadDetail.whatsapp.quickMessages.subtitle') }}
          </span>
        </div>
      </div>
      <button
        type="button"
        class="wa-quick-messages-popover__action-btn wa-quick-messages-popover__action-btn--new"
        :disabled="saving"
        @click="openCreateModal"
      >
        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"/>
        </svg>
        {{ t('kanban.leadDetail.whatsapp.quickMessages.new') }}
      </button>
    </div>

    <div class="wa-quick-messages-popover__body">
      <div v-if="loading" class="wa-quick-messages-popover__state">
        <span class="wa-quick-messages-popover__state-spinner" aria-hidden="true" />
        {{ t('common.loading') }}
      </div>
      <div v-else-if="!messages.length" class="wa-quick-messages-popover__state wa-quick-messages-popover__state--empty">
        <span class="wa-quick-messages-popover__empty-icon" aria-hidden="true">
          <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"/>
          </svg>
        </span>
        <p class="wa-quick-messages-popover__empty-title">{{ t('kanban.leadDetail.whatsapp.quickMessages.empty') }}</p>
        <p class="wa-quick-messages-popover__empty-hint">{{ t('kanban.leadDetail.whatsapp.quickMessages.emptyHint') }}</p>
      </div>
      <ul v-else class="wa-quick-messages-popover__list" role="listbox">
        <li
          v-for="item in messages"
          :key="item.id"
          role="option"
          class="wa-quick-messages-popover__item"
          :class="{ 'wa-quick-messages-popover__item--selected': selectedId === item.id }"
          :aria-selected="selectedId === item.id"
          @click="onSelectItem(item)"
        >
          <span class="wa-quick-messages-popover__item-badge" aria-hidden="true">
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 8h10M7 12h6m-6 4h10M5 6a2 2 0 012-2h10a2 2 0 012 2v12a2 2 0 01-2 2H7a2 2 0 01-2-2V6z"/>
            </svg>
          </span>
          <span class="wa-quick-messages-popover__item-text">{{ item.texto }}</span>
          <button
            type="button"
            class="wa-quick-messages-popover__item-delete"
            :title="t('kanban.leadDetail.whatsapp.quickMessages.delete')"
            :aria-label="t('kanban.leadDetail.whatsapp.quickMessages.delete')"
            :disabled="deleting"
            @click.stop="confirmDelete(item.id)"
          >
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </li>
      </ul>
    </div>

    <div v-if="!loading && messages.length" class="wa-quick-messages-popover__footer">
      {{ t('kanban.leadDetail.whatsapp.quickMessages.footerHint') }}
    </div>

    <Teleport to="body">
      <div
        v-if="showCreateModal"
        class="wa-quick-messages-modal-overlay"
        role="dialog"
        aria-modal="true"
        :aria-label="t('kanban.leadDetail.whatsapp.quickMessages.createTitle')"
        @click="closeCreateModal"
      >
        <div class="wa-quick-messages-modal" @click.stop>
          <div class="wa-quick-messages-modal__header">
            <h3 class="wa-quick-messages-modal__title">{{ t('kanban.leadDetail.whatsapp.quickMessages.createTitle') }}</h3>
            <button type="button" class="wa-quick-messages-modal__close" :aria-label="t('common.close')" @click="closeCreateModal">
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          <div class="wa-quick-messages-modal__body">
            <div class="wa-quick-messages-modal__textarea-wrap">
              <textarea
                ref="createTextareaRef"
                v-model="createText"
                rows="4"
                class="wa-quick-messages-modal__textarea"
                :placeholder="t('kanban.leadDetail.whatsapp.quickMessages.createPlaceholder')"
                maxlength="2000"
              />
              <div class="wa-quick-messages-modal__emoji-anchor">
                <button
                  ref="createEmojiBtnRef"
                  type="button"
                  class="wa-composer-emoji-btn"
                  :title="t('kanban.leadDetail.whatsapp.insertEmoji')"
                  :aria-label="t('kanban.leadDetail.whatsapp.insertEmoji')"
                  @click.stop="toggleCreateEmojiPicker"
                >
                  <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M14.828 14.828a4 4 0 01-5.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
          <div class="wa-quick-messages-modal__footer">
            <button type="button" class="wa-quick-messages-modal__btn wa-quick-messages-modal__btn--ghost" @click="closeCreateModal">
              {{ t('common.cancel') }}
            </button>
            <button
              type="button"
              class="wa-quick-messages-modal__btn wa-quick-messages-modal__btn--primary"
              :disabled="saving || !createText.trim()"
              @click="saveCreate"
            >
              {{ saving ? t('common.saving') : t('kanban.leadDetail.whatsapp.quickMessages.createSave') }}
            </button>
          </div>
        </div>
      </div>
    </Teleport>

    <Teleport to="body">
      <EmojiPickerPopover
        v-if="showCreateModal && showCreateEmojiPicker"
        class="wa-quick-messages-modal__emoji-picker wa-quick-messages-modal__emoji-picker--fixed"
        :style="createEmojiPickerStyle"
        @select="insertCreateEmoji"
        @close="showCreateEmojiPicker = false"
      />
    </Teleport>
  </div>
</template>

<script setup>
import { nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import Swal from 'sweetalert2'
import { useToast } from 'vue-toastification'
import EmojiPickerPopover from './EmojiPickerPopover.vue'

const props = defineProps({
  messages: { type: Array, default: () => [] },
  loading: { type: Boolean, default: false },
  saving: { type: Boolean, default: false },
  deleting: { type: Boolean, default: false },
  selectedId: { type: [Number, null], default: null },
  onCreate: { type: Function, default: null },
  onDelete: { type: Function, default: null }
})

const emit = defineEmits(['select', 'close', 'update:selectedId'])

const { t } = useI18n()
const toast = useToast()

const rootRef = ref(null)
const showCreateModal = ref(false)
const showCreateEmojiPicker = ref(false)
const createText = ref('')
const createTextareaRef = ref(null)
const createEmojiBtnRef = ref(null)
const createEmojiPickerStyle = ref({})

const PICKER_EST_HEIGHT = 300
const PICKER_EST_WIDTH = 320

const updateCreateEmojiPickerPosition = () => {
  const btn = createEmojiBtnRef.value
  if (!btn) return

  const rect = btn.getBoundingClientRect()
  const pickerW = Math.min(PICKER_EST_WIDTH, window.innerWidth - 16)
  let top = rect.top - PICKER_EST_HEIGHT - 8
  let left = rect.right - pickerW

  if (top < 8) {
    top = rect.bottom + 8
  }
  left = Math.max(8, Math.min(left, window.innerWidth - pickerW - 8))

  createEmojiPickerStyle.value = {
    position: 'fixed',
    top: `${Math.round(top)}px`,
    left: `${Math.round(left)}px`,
    width: `${pickerW}px`,
    zIndex: 10072
  }
}

const toggleCreateEmojiPicker = async () => {
  if (showCreateEmojiPicker.value) {
    showCreateEmojiPicker.value = false
    return
  }
  showCreateEmojiPicker.value = true
  await nextTick()
  updateCreateEmojiPickerPosition()
}

const insertCreateEmoji = async (emoji) => {
  const input = createTextareaRef.value
  const current = createText.value || ''
  if (input && typeof input.selectionStart === 'number') {
    const start = input.selectionStart
    const end = input.selectionEnd ?? start
    createText.value = current.slice(0, start) + emoji + current.slice(end)
    await nextTick()
    const pos = start + emoji.length
    input.focus()
    input.setSelectionRange(pos, pos)
  } else {
    createText.value = current + emoji
    await nextTick()
    createTextareaRef.value?.focus()
  }
  showCreateEmojiPicker.value = false
}

const openCreateModal = async () => {
  createText.value = ''
  showCreateEmojiPicker.value = false
  showCreateModal.value = true
  await nextTick()
  createTextareaRef.value?.focus()
}

const closeCreateModal = () => {
  if (props.saving) return
  showCreateModal.value = false
  showCreateEmojiPicker.value = false
  createText.value = ''
}

const saveCreate = async () => {
  const texto = createText.value.trim()
  if (!texto || !props.onCreate) return
  try {
    await props.onCreate(texto)
    toast.success(t('kanban.leadDetail.whatsapp.quickMessages.created'))
    closeCreateModal()
  } catch (err) {
    const apiMsg = err?.response?.data?.error
    await Swal.fire({
      title: t('common.error'),
      text: apiMsg || t('kanban.leadDetail.whatsapp.quickMessages.createError'),
      icon: 'error',
      confirmButtonText: 'OK'
    })
  }
}

const onSelectItem = (item) => {
  emit('update:selectedId', item.id)
  emit('select', item.texto)
}

const confirmDelete = async (mensagemId) => {
  if (!mensagemId) return

  const result = await Swal.fire({
    title: t('kanban.leadDetail.whatsapp.quickMessages.deleteTitle'),
    text: t('kanban.leadDetail.whatsapp.quickMessages.deleteConfirm'),
    icon: 'warning',
    showCancelButton: true,
    confirmButtonColor: '#ef4444',
    cancelButtonColor: '#6b7280',
    confirmButtonText: t('kanban.leadDetail.whatsapp.quickMessages.delete'),
    cancelButtonText: t('common.cancel'),
    reverseButtons: true
  })

  if (!result.isConfirmed) return

  try {
    if (!props.onDelete) return
    await props.onDelete(mensagemId)
    toast.success(t('kanban.leadDetail.whatsapp.quickMessages.deleted'))
  } catch (err) {
    const apiMsg = err?.response?.data?.error
    await Swal.fire({
      title: t('common.error'),
      text: apiMsg || t('kanban.leadDetail.whatsapp.quickMessages.deleteError'),
      icon: 'error',
      confirmButtonText: 'OK'
    })
  }
}

const onDocumentPointerDown = (event) => {
  if (showCreateModal.value) return
  if (event.target?.closest?.('.wa-quick-messages-modal-overlay')) return
  const el = rootRef.value
  if (el && !el.contains(event.target)) {
    emit('close')
  }
}

watch(showCreateEmojiPicker, (open) => {
  if (open) {
    window.addEventListener('resize', updateCreateEmojiPickerPosition)
    window.addEventListener('scroll', updateCreateEmojiPickerPosition, true)
  } else {
    window.removeEventListener('resize', updateCreateEmojiPickerPosition)
    window.removeEventListener('scroll', updateCreateEmojiPickerPosition, true)
  }
})

onMounted(() => {
  document.addEventListener('pointerdown', onDocumentPointerDown, true)
})

onBeforeUnmount(() => {
  document.removeEventListener('pointerdown', onDocumentPointerDown, true)
  window.removeEventListener('resize', updateCreateEmojiPickerPosition)
  window.removeEventListener('scroll', updateCreateEmojiPickerPosition, true)
})
</script>
